import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { getCoreRowModel } from '@tanstack/react-table';

// MUI Theme
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Button, Paper } from '@mui/material';
import { FileUploadOutlined } from '@mui/icons-material';
import { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';

// Utils
import { FormatForApi, shortDateFormat } from '../../../utils/Format';

// Services
import { getSuperSentinelBulkImportDetails, uploadSuperSentinelBulkActivation } from '../../../services/superSentinel.services';
import { makeManageSuperSentinelBulkActivationColumn } from '../../../utils/columns/makeManageSuperSentinelBulkActivationColumn';

// Types
import { InventoryContent, SearchBarFilterTypes } from '../../../constants/Constants';
import { ImportedFileData } from '../../../types/ImportedFileData';
import { FilterableTypes } from '../../../types/FilterableTypes';

// Storage
import { Storage } from '../../../utils/Storage';

// Hooks
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';

// Common Components
import { useStandardTable as useTable, Table } from '../../common/table';
import FormInputCollection from '../../common/FormInputCollection/FormInputCollection';
import CustomModal from '../../common/withModal';
import SearchBar from '../../common/SearchBar/SearchBar';
import UploadForm from '../../common/forms/Upload/uploadForm';

// Components
import SuperSentinelBulkActivationDetails from './components/SuperSentinelBulkActivationDetails';
import { tablePrimaryButtonSx } from '../../../styles/tableButtonStyles';

const DEFAULT_SORT = [{ createdDateTime: -1 }];

const useStyles = makeStyles<Theme>((theme) => ({
  heading: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0 10px',
    borderBottom: '1px solid #ccc',

    [theme.breakpoints.up('lg')]: {
      height: '60px',
    },
  },

  syncIcon: {
    fontSize: 20,
  },

  uploadBtn: {
    textTransform: 'none',
  },

  searchContentWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    marginBottom: 5,
  },
}));

const SuperSentinelActivation = () => {
  const classes = useStyles();
  const { notifyMessage } = useNotifyMessage();

  const [lastSeenDateTimeFrom, setLastSeenDateTimeFrom] = useState<Date | null>(null);
  const [lastSeenDateTimeTo, setLastSeenDateTimeTo] = useState<Date | null>(null);

  const [changedInventoryDetail, setChangedInventoryDetail] = useState<any>();

  const [paramFilter, setParamFilter] = useState<any>(() => {
    const filterState = Storage.getSearchBarFiltering(SearchBarFilterTypes.DEVICE_INVENTORY);
    return filterState ? { filters: filterState.filters, sortBy: filterState.sortBy ?? DEFAULT_SORT } : { filters: [], sortBy: DEFAULT_SORT };
  });

  const [isResetting, setIsResetting] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [openModalType, setOpenModalType] = useState<'upload' | 'summary' | null>(null);

  const forceReload = useCallback(() => {
    setReloadTrigger((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (isResetting) {
      Storage.setSearchBarFiltering(SearchBarFilterTypes.DEVICE_INVENTORY, {
        filters: [],
      });

      setParamFilter({
        filters: [],
        sortBy: paramFilter.sortBy ?? DEFAULT_SORT,
      });

      setIsResetting(false);
    }
  }, [isResetting, paramFilter.sortBy]);

  const handleViewSummary = (data: ImportedFileData): void => {
    setChangedInventoryDetail(data);
    setOpenModalType('summary');
  };

  const handleReset = () => {
    setIsResetting(true);
    setLastSeenDateTimeFrom(null);
    setLastSeenDateTimeTo(null);
  };

  const onSearchChange = (filters: any[]) => {
    if (lastSeenDateTimeFrom && lastSeenDateTimeTo) {
      filters.push({
        createdDateTime: FormatForApi(moment(lastSeenDateTimeFrom), true),
        filterType: FilterableTypes.GreaterThanOrEqual,
        and: {
          createdDateTime: FormatForApi(moment(lastSeenDateTimeTo), false),
          filterType: FilterableTypes.LessThanOrEqual,
        },
      });
    }

    setParamFilter({
      filters,
      sortBy: paramFilter.sortBy ?? DEFAULT_SORT,
    });

    Storage.setSearchBarFiltering(SearchBarFilterTypes.DEVICE_INVENTORY, {
      isMoreFilter: true,
      filters,
    });
  };

  /**
   * Called by UploadForm after the upload promise resolves or rejects.
   *
   * UploadForm passes `files` on success and an `Error` on failure.
   * We surface both cases through useNotifyMessage so the user always
   * gets a clear, readable notification.
   */
  const handleFilesUpdate = useCallback(
    (files: any[], error?: Error | string) => {
      // --- Error path ---
      if (error) {
        const message =
          error instanceof Error
            ? error.message
            : typeof error === 'string'
              ? error
              : 'An unexpected error occurred during upload. Please try again.';
        notifyMessage(message, 'error');
        return;
      }

      if (!files || files.length === 0) return;

      // --- Success path ---
      notifyMessage('File uploaded successfully', 'success');
      setOpenModalType(null);
      setTimeout(forceReload, 2000);
    },
    [forceReload, notifyMessage],
  );

  /**
   * Wrapped uploadAsync passed to UploadForm.
   *
   * UploadForm swallows errors thrown by uploadAsync and never forwards them
   * to onFilesUpdate. By wrapping here we intercept any validation error
   * (empty file, bad column names, wrong file type, etc.) and surface it
   * immediately via useNotifyMessage before UploadForm ever sees it.
   */
  const wrappedUploadAsync = useCallback(
    async (file: File, onUploadProgress: (progressEvent: ProgressEvent) => void) => {
      try {
        return await uploadSuperSentinelBulkActivation(file, onUploadProgress);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'An unexpected error occurred during upload. Please try again.';
        notifyMessage(message, 'error');
        // Re-throw so UploadForm knows the upload did not succeed
        throw err;
      }
    },
    [notifyMessage],
  );

  const columns = useMemo(() => makeManageSuperSentinelBulkActivationColumn(handleViewSummary), []);

  const fetchAsync = useMemo(() => getSuperSentinelBulkImportDetails(), []);

  const params = useMemo(() => ({ ...paramFilter, reloadTrigger }), [paramFilter, reloadTrigger]);

  const table = useTable<any>({
    columns,
    fetchAsync,
    params,
    serverSide: true,
    shouldFetch: true,
  });

  return (
    <>
      <div className={classes.heading}>
        <h4>Manage Super Sentinel Bulk Activation</h4>

        <div>
          <Button
            variant='contained'
            size='small'
            className={classes.uploadBtn}
            onClick={() => setOpenModalType('upload')}
            endIcon={<FileUploadOutlined className={classes.syncIcon} />}
            sx={tablePrimaryButtonSx}
          >
            Upload CSV
          </Button>
        </div>
      </div>

      <SearchBar
        pageKey={SearchBarFilterTypes.DEVICE_INVENTORY}
        onSearch={onSearchChange}
        onReset={handleReset}
        columns={columns}
        allowSearchAll={true}
        components={
          <div className={classes.searchContentWrapper}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <FormInputCollection label='Created Date'>
                <div style={{ marginRight: '8px' }}>
                  <DatePicker
                    label='From'
                    value={lastSeenDateTimeFrom ? moment(lastSeenDateTimeFrom) : null}
                    format={shortDateFormat}
                    sx={{ width: '185px' }}
                    slotProps={{ textField: { size: 'small' } }}
                    onChange={(date) => setLastSeenDateTimeFrom(date ? date.toDate() : null)}
                  />
                </div>

                <DatePicker
                  label='To'
                  value={lastSeenDateTimeTo ? moment(lastSeenDateTimeTo) : null}
                  format={shortDateFormat}
                  sx={{ width: '185px' }}
                  slotProps={{ textField: { size: 'small' } }}
                  onChange={(date) => setLastSeenDateTimeTo(date ? date.toDate() : null)}
                />
              </FormInputCollection>
            </LocalizationProvider>
          </div>
        }
      />

      {openModalType === 'summary' && (
        <CustomModal
          isOpen={!!changedInventoryDetail}
          setIsOpen={() => setOpenModalType(null)}
          showSaveButton={false}
          maxWidth='md'
          customAction={() => (
            <Button variant='contained' onClick={() => setOpenModalType(null)} color='secondary' size='small'>
              {InventoryContent.close}
            </Button>
          )}
        >
          <SuperSentinelBulkActivationDetails importId={changedInventoryDetail?.id} />
        </CustomModal>
      )}

      {openModalType === 'upload' && (
        <CustomModal
          title='Upload Super Sentinel CSV'
          isOpen={openModalType === 'upload'}
          setIsOpen={() => setOpenModalType(null)}
          maxWidth='md'
          customAction={() => (
            <Button variant='contained' onClick={() => setOpenModalType(null)} color='secondary' size='small'>
              {InventoryContent.close}
            </Button>
          )}
        >
          <UploadForm onFilesUpdate={handleFilesUpdate} fileType='excel' uploadAsync={wrappedUploadAsync} />
        </CustomModal>
      )}

      <Paper>
        <Table {...table} showSearchBar={false} showColumnToggler={false} getCoreRowModel={getCoreRowModel()} />
      </Paper>
    </>
  );
};

export default SuperSentinelActivation;
